import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { DashboardReservationColumn, DashboardReservationColumnName } from '@/models/dashboard/dashboard-reservation'
import { draggableIconStyle } from '@/components/molecules/settings/use-draggable-dom'

type Props = {
  column: DashboardReservationColumn
  onToggleVisibility: () => void
  dragStart: (e: React.DragEvent<HTMLDivElement>) => void
  dragOver: (e: React.DragEvent<HTMLDivElement>) => void
  dragDrop: (e: React.DragEvent<HTMLDivElement>) => void
  handleTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void
  handleTouchMove?: (e: React.TouchEvent<HTMLDivElement>) => void
  handleTouchEnd?: (e: React.TouchEvent<HTMLDivElement>) => void
}

export const ColumnSettingItem: React.FC<Props> = ({
  column,
  onToggleVisibility,
  dragStart,
  dragOver,
  dragDrop,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
}) => {
  const { t } = useTranslation()

  /**
   * カラム名から言語キーを生成して、各言語のラベルを取得
   * @param columnName カラム名
   * @returns 言語ファイルから取得したラベルテキスト
   */
  const getColumnLabel = (columnName: DashboardReservationColumnName): string => {
    const key = `dashboard.columns.${columnName}`
    return t(key)
  }

  return (
    <div
      id={column.id}
      // 固定カラムのスタイルと編集可能カラム（ドラッグアイコン表示用）のスタイルを切り替え
      css={[columnItemStyle, column.isFixed ? fixedColumnStyle : draggableIconStyle]}
      // 固定カラムはドラッグ不可、編集可能カラムのみドラッグ可能
      draggable={!column.isFixed}
      onDragStart={!column.isFixed ? dragStart : undefined}
      onDragOver={!column.isFixed ? dragOver : undefined}
      onDrop={!column.isFixed ? dragDrop : undefined}
      onTouchStart={!column.isFixed ? handleTouchStart : undefined}
      onTouchMove={!column.isFixed ? handleTouchMove : undefined}
      onTouchEnd={!column.isFixed ? handleTouchEnd : undefined}
    >
      {/* カラムラベル表示 */}
      <div css={columnContentStyle}>
        <span css={columnLabelStyle}>
          {getColumnLabel(column.columnName)}
          {/* 固定カラムの場合は「(固定)」ラベルを表示 */}
          {column.isFixed && <span css={fixedLabelStyle}> ({t('dashboard.fixed')})</span>}
        </span>
      </div>
      {/* 表示/非表示切り替えアイコン（固定カラムはクリック不可） */}
      <div css={eyeIconStyle} onClick={column.isFixed ? undefined : onToggleVisibility}>
        <img
          src={column.isVisible ? require('@/static/images/icon_eye_on.svg') : require('@/static/images/icon_eye_off.svg')}
          alt={column.isVisible ? 'visible' : 'hidden'}
          css={column.isFixed ? disabledEyeIconStyle : undefined}
        />
      </div>
    </div>
  )
}

const columnItemStyle = css({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  marginBottom: 8,
  backgroundColor: '#FFF',
  borderRadius: 10,
  border: '1px solid #E0E0E0',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#FAFAFA',
  },
})

const fixedColumnStyle = css({
  backgroundColor: '#E8E8E8',
  border: '1px solid #CCCCCC',
  '&:hover': {
    backgroundColor: '#E0E0E0',
  },
})

const columnContentStyle = css({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  paddingLeft: 40,
})

const columnLabelStyle = css({
  fontSize: 14,
  color: '#272727',
  fontWeight: 'bold',
})

const fixedLabelStyle = css({
  fontSize: 12,
  color: '#999999',
  marginLeft: 4,
})

const eyeIconStyle = css({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: 4,
  '&:hover': {
    opacity: 0.7,
  },
  img: {
    width: 20,
    height: 20,
  },
})

const disabledEyeIconStyle = css({
  opacity: 0.4,
  cursor: 'not-allowed',
  '&:hover': {
    opacity: 0.4,
  },
})
